export class SystemTypes {
    static IN_SCOPE = 'main'
    static HR = 'hr'
    static TICKET = 'ticket'
}

const STORAGE_KEY = 'auditech:objectiveSystemRequirements'

// Default system requirements per objective number
const DEFAULT_REQUIREMENTS: Record<number, string[]> = {
    1:  [SystemTypes.IN_SCOPE, SystemTypes.TICKET, SystemTypes.HR],   // Manager Approval
    2:  [SystemTypes.IN_SCOPE, SystemTypes.TICKET, SystemTypes.HR],   // Change Requests
    3:  [SystemTypes.IN_SCOPE, SystemTypes.HR, SystemTypes.TICKET],   // Developers Access to Production
    4:  [SystemTypes.IN_SCOPE, SystemTypes.TICKET, SystemTypes.HR],   // QA Approvals
    5:  [SystemTypes.IN_SCOPE],                                        // Segregation of Environments
    6:  [SystemTypes.IN_SCOPE, SystemTypes.TICKET, SystemTypes.HR],   // UAT Approvals
    9:  [SystemTypes.IN_SCOPE, SystemTypes.HR],                        // Admin Users Review
    11: [SystemTypes.IN_SCOPE, SystemTypes.HR],                        // Key Financial Folders
    12: [SystemTypes.IN_SCOPE],                                        // Password Settings
    13: [SystemTypes.HR, SystemTypes.IN_SCOPE],                        // Off-Boarding
    14: [SystemTypes.IN_SCOPE, SystemTypes.HR, SystemTypes.TICKET],   // On-Boarding
    15: [SystemTypes.IN_SCOPE, SystemTypes.HR],                        // Users Review
    26: [SystemTypes.IN_SCOPE, SystemTypes.HR],                        // Administration Activities Review
    36: [SystemTypes.IN_SCOPE, SystemTypes.HR],                        // Jobs Admin Users Review
    37: [SystemTypes.IN_SCOPE, SystemTypes.TICKET, SystemTypes.HR],   // Role Assignment
    38: [SystemTypes.IN_SCOPE],                                        // Segregation of Duties
    40: [SystemTypes.IN_SCOPE],                                        // Database Password Settings
    41: [SystemTypes.IN_SCOPE],                                        // Database Users Review
    42: [SystemTypes.IN_SCOPE],                                        // Database Admin Users Review
    43: [SystemTypes.IN_SCOPE],                                        // Database Version Update
    46: [SystemTypes.IN_SCOPE, SystemTypes.HR],                        // SAP Admin User Review (Profile)
    47: [SystemTypes.IN_SCOPE, SystemTypes.HR],                        // SAP Admin User Review (Transaction)
    48: [SystemTypes.IN_SCOPE],                                        // CUA Role Assignment
    49: [SystemTypes.IN_SCOPE],                                        // Database Public Server Permissions
    57: [SystemTypes.IN_SCOPE, SystemTypes.HR],                        // Invoice Approvers Review
    62: [SystemTypes.IN_SCOPE],                                        // Branch Protection Rules
}

/** Load overrides from localStorage, merged with defaults */
const loadRequirements = (): Record<number, string[]> => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) {
            const overrides = JSON.parse(stored) as Record<string, string[]>
            const merged = { ...DEFAULT_REQUIREMENTS }
            for (const [key, val] of Object.entries(overrides)) {
                merged[Number(key)] = val
            }
            return merged
        }
    } catch { /* ignore parse errors */ }
    return { ...DEFAULT_REQUIREMENTS }
}

// Mutable runtime map — initialized from defaults + localStorage overrides
let objectiveSystemOrder: Record<number, string[]> = loadRequirements()

export const isRequiredSystem = (objectiveNum: number | null, systemType: string): boolean => {
    if (objectiveNum === null || !(objectiveNum in objectiveSystemOrder)) return systemType === SystemTypes.IN_SCOPE
    return objectiveSystemOrder[objectiveNum].includes(systemType)
}

/** Get the required system types for an objective */
export const getRequiredSystems = (objectiveNum: number | null): string[] => {
    if (objectiveNum === null || !(objectiveNum in objectiveSystemOrder)) return [SystemTypes.IN_SCOPE]
    return [...objectiveSystemOrder[objectiveNum]]
}

/** Update required systems for an objective and persist to localStorage */
export const setRequiredSystems = (objectiveNum: number, systems: string[]): void => {
    objectiveSystemOrder[objectiveNum] = systems.length > 0 ? systems : [SystemTypes.IN_SCOPE]
    // Save all overrides (diff from defaults would be complex; just save all)
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(objectiveSystemOrder))
    } catch { /* storage full or unavailable */ }
}

/** Get the full requirements map (for display in objectives table) */
export const getAllRequirements = (): Record<number, string[]> => {
    return { ...objectiveSystemOrder }
}
